import { ref, watch } from 'vue'
import { SfuClient } from '../services/sfuClient'
import { useAvatarTransform } from './useAvatarTransform'
import { useVideoChatSettingsStore } from '../../stores/videoChatSettingsStore'
import { createDebugLogger, isDebugLogEnabled } from '../../config/debugLog'

const sfuDebug = createDebugLogger('sfu')

export function useSfuRoom({ conversationId, peerId }) {
  const client = new SfuClient({ conversationId, peerId })
  const localStream = ref(null)
  const remoteStreams = ref([]) // array of { peerId, stream }
  const remotePeers = ref([]) // array of peerIds in room (excluding self)
  const joined = ref(false)
  const publishing = ref(false)
  const {
    startTransformed,
    startFromRaw,
    stop: stopTransform,
    updateSettings
  } = useAvatarTransform()
  const vcStore = useVideoChatSettingsStore?.() || null
  // Track the latest video producerId per remote peer for layer control
  const videoProducerByPeer = new Map()

  async function join(overrides = {}) {
    try {
      if (overrides && (overrides.conversationId || overrides.peerId)) {
        if (overrides.conversationId)
          client.conversationId = String(overrides.conversationId || '').trim()
        if (overrides.peerId) client.peerId = String(overrides.peerId || '').trim()
      }
    } catch {}
    sfuDebug('[SFU] join() using', client.conversationId, client.peerId)
    // Register all handlers BEFORE connect() so we don't miss messages
    // that arrive immediately after the 'joined' response (e.g. peerList).
    client.on('peerList', ({ peers }) => {
      try {
        const list = Array.isArray(peers) ? peers : []
        remotePeers.value = list.filter((id) => id && id !== client.peerId)
        sfuDebug('[SFU] peerList =>', remotePeers.value)
      } catch {}
    })
    client.on('peerJoined', ({ peerId: id }) => {
      try {
        const pid = String(id || '')
        if (!pid || pid === client.peerId) return
        if (!remotePeers.value.includes(pid)) remotePeers.value = [...remotePeers.value, pid]
        sfuDebug('[SFU] peerJoined', pid, 'peers=', remotePeers.value)
      } catch {}
    })
    client.on('peerLeft', ({ peerId: id }) => {
      try {
        const pid = String(id || '')
        if (!pid) return
        remotePeers.value = remotePeers.value.filter((x) => x !== pid)
        remoteStreams.value = remoteStreams.value.filter((r) => r.peerId !== pid)
        sfuDebug('[SFU] peerLeft', pid, 'peers=', remotePeers.value)
      } catch {}
    })
    client.on('newProducer', async ({ producerId, peerId: from, kind }) => {
      try {
        const peerIdFromProducer = String(from || '')
        if (kind === 'video' && peerIdFromProducer) {
          try {
            videoProducerByPeer.set(peerIdFromProducer, String(producerId || ''))
          } catch {}
        }
        // Default to low spatial layer for grid tiles; UI can upgrade later.
        const preferLow = kind === 'video'
        const stream = await client.subscribe(producerId, {
          paused: false,
          preferredLayers: preferLow ? { spatialLayer: 0 } : undefined
        })
        if (!stream) {
          sfuDebug('[SFU] newProducer: subscribe returned null for', producerId)
          return
        }

        const incomingTracks = stream.getTracks?.() || []
        const current = Array.isArray(remoteStreams.value) ? remoteStreams.value : []
        const next = [...current]
        const existingIdx = next.findIndex((entry) => entry.peerId === peerIdFromProducer)

        let combinedStream = null
        if (existingIdx < 0) {
          // First producer for this peer: keep returned stream directly.
          combinedStream = stream
          next.push({ peerId: peerIdFromProducer, stream: combinedStream })
        } else {
          const existingEntry = next[existingIdx] || { peerId: peerIdFromProducer, stream: null }
          combinedStream = existingEntry.stream || new MediaStream()
          incomingTracks.forEach((track) => {
            const sameKind = combinedStream.getTracks().filter((t) => t.kind === track.kind)
            sameKind.forEach((t) => combinedStream.removeTrack(t))
            combinedStream.addTrack(track)
          })
          next[existingIdx] = {
            ...existingEntry,
            peerId: peerIdFromProducer,
            stream: combinedStream
          }
        }

        remoteStreams.value = next

        if (
          peerIdFromProducer &&
          peerIdFromProducer !== client.peerId &&
          !remotePeers.value.includes(peerIdFromProducer)
        ) {
          remotePeers.value = [...remotePeers.value, peerIdFromProducer]
        }

        const kinds = (combinedStream?.getTracks?.() || []).map((t) => t.kind)
        sfuDebug('[SFU] remote stream merged', {
          peerId: peerIdFromProducer,
          producerId,
          kind,
          kinds,
          remoteStreamsUpdated: true
        })
      } catch (e) {
        console.warn('[SFU] newProducer error:', e?.message || e)
      }
    })
    // Now that handlers are registered, connect (which triggers join + peerList)
    await client.connect()
    joined.value = true
  }

  // Live-apply store updates to the running transform
  if (vcStore) {
    try {
      watch(
        () => ({
          lv: vcStore.settings?.layerVisibility,
          seg: vcStore.settings?.useSegmentation,
          eye: vcStore.settings?.eye,
          iris: vcStore.settings?.irisStyle,
          bgId: vcStore.settings?.backgroundId,
          bgSrc: vcStore.settings?.backgroundSrc,
          avId: vcStore.settings?.avatarId,
          avSrc: vcStore.settings?.avatarSrc,
          expressionPresetId: vcStore.settings?.expressionPresetId,
          expressionStrength: vcStore.settings?.expressionStrength,
          faceFitStrength: vcStore.settings?.faceFitStrength,
          mouthBoost: vcStore.settings?.mouthBoost,
          mouthSmoothing: vcStore.settings?.mouthSmoothing,
          browStrength: vcStore.settings?.browStrength,
          browSmoothing: vcStore.settings?.browSmoothing,
          cheekStrength: vcStore.settings?.cheekStrength,
          cheekSmoothing: vcStore.settings?.cheekSmoothing,
          cutoutSrc: vcStore.settings?.precomputedAvatarCutoutSrc,
          hairMaskSrc: vcStore.settings?.precomputedAvatarHairMaskSrc
        }),
        async (v) => {
          try {
            await updateSettings(
              toSafeTransformOptions({
                layerVisibility: v.lv,
                useSegmentation: v.seg,
                eye: v.eye,
                irisStyle: v.iris,
                backgroundId: v.bgId,
                backgroundSrc: v.bgSrc,
                avatarId: v.avId,
                avatarSrc: v.avSrc,
                expressionPresetId: v.expressionPresetId,
                expressionStrength: v.expressionStrength,
                faceFitStrength: v.faceFitStrength,
                mouthBoost: v.mouthBoost,
                mouthSmoothing: v.mouthSmoothing,
                browStrength: v.browStrength,
                browSmoothing: v.browSmoothing,
                cheekStrength: v.cheekStrength,
                cheekSmoothing: v.cheekSmoothing,
                precomputedAvatarCutoutSrc: v.cutoutSrc,
                precomputedAvatarHairMaskSrc: v.hairMaskSrc
              })
            )
          } catch {}
        },
        { deep: true }
      )
    } catch {}
  }

  async function startLocal(
    constraints = {
      audio: {
        echoCancellation: true,
        noiseSuppression: false,
        autoGainControl: false,
        channelCount: 1,
        sampleRate: 48000,
        sampleSize: 16
      },
      video: {
        width: { ideal: 1280 },
        height: { ideal: 720 },
        frameRate: { ideal: 30 },
        facingMode: 'user'
      }
    }
  ) {
    try {
      sfuDebug('[SFU][local] startLocal: requesting raw getUserMedia', constraints)
      // Acquire raw camera first for instant preview
      const raw = await navigator.mediaDevices.getUserMedia(constraints)
      // Reinforce audio constraints post-capture on supported browsers
      try {
        const at = raw?.getAudioTracks?.()[0]
        if (at && typeof at.applyConstraints === 'function') {
          await at.applyConstraints({
            echoCancellation: true,
            noiseSuppression: false,
            autoGainControl: false,
            channelCount: 1,
            sampleRate: 48000,
            sampleSize: 16
          })
        }
      } catch {}
      try {
        const vs = raw?.getVideoTracks?.()[0]?.getSettings?.() || {}
        const as = raw?.getAudioTracks?.()[0]?.getSettings?.() || {}
        sfuDebug('[SFU][local] got raw stream', { video: vs, audio: as })
      } catch {}
      // localStream.value = raw <-- DELAYED to match legacy behavior (wait for transform)

      // Start avatar transform in background and swap video track when ready
      const baseOpts = vcStore
        ? toSafeTransformOptions(vcStore.settings || {})
        : toSafeTransformOptions({})
      sfuDebug('[SFU][transform] starting transform with options', baseOpts)

      const transformed = await startFromRaw(raw, baseOpts)
      // Only swap once the transformed stream is actually producing frames
      const frameOk = await waitForFirstFrame(transformed, 2500)

      if (frameOk) {
        localStream.value = transformed
        sfuDebug('[SFU][local] publishing transformed stream')
        await client.publish(localStream.value)
      } else {
        console.warn('[SFU][local] transformed stream timed out, falling back to raw')
        try {
          transformed.getTracks().forEach((t) => t.stop())
        } catch {}
        try {
          stopTransform()
        } catch {}
        localStream.value = raw
        sfuDebug('[SFU][local] publishing raw stream')
        await client.publish(localStream.value)
      }
      publishing.value = true
    } catch (e) {
      publishing.value = false
      throw e
    }
  }

  function leave() {
    sfuDebug('[SFU] leave() called')
    client.close()
    joined.value = false
    publishing.value = false
    try {
      localStream.value?.getTracks?.().forEach((t) => t.stop())
    } catch {}
    try {
      stopTransform()
    } catch {}
    localStream.value = null
    remoteStreams.value = []
    remotePeers.value = []
    try {
      videoProducerByPeer.clear()
    } catch {}
  }

  // Helpers for UI to adjust layers/pause by peerId
  async function setPeerVideoLayer(peerId, spatialLayer = 0, temporalLayer) {
    try {
      const pid = videoProducerByPeer.get(String(peerId || ''))
      if (!pid) return
      await client.setPreferredLayersForProducer(pid, { spatialLayer, temporalLayer })
    } catch {}
  }
  async function pausePeerVideo(peerId) {
    try {
      const pid = videoProducerByPeer.get(String(peerId || ''))
      if (!pid) return
      await client.pauseForProducer(pid)
    } catch {}
  }
  async function resumePeerVideo(peerId) {
    try {
      const pid = videoProducerByPeer.get(String(peerId || ''))
      if (!pid) return
      await client.resumeForProducer(pid)
    } catch {}
  }

  return {
    client,
    localStream,
    remoteStreams,
    remotePeers,
    joined,
    publishing,
    join,
    startLocal,
    leave,
    setPeerVideoLayer,
    pausePeerVideo,
    resumePeerVideo,
    updateSettings,
    vcStore
  }
}

export default { useSfuRoom }

function toTransformOptions(settings = {}) {
  const out = {}
  if (settings.layerVisibility)
    out.layerVisibility = JSON.parse(JSON.stringify(settings.layerVisibility))
  if (typeof settings.useSegmentation !== 'undefined')
    out.useSegmentation = !!settings.useSegmentation
  if (settings.eye) out.eye = JSON.parse(JSON.stringify(settings.eye))
  if (settings.irisStyle) out.irisStyle = settings.irisStyle
  if (typeof settings.backgroundId !== 'undefined') out.backgroundId = settings.backgroundId || ''
  if (typeof settings.backgroundSrc !== 'undefined')
    out.backgroundSrc = settings.backgroundSrc || null
  if (settings.avatarId) out.avatarId = settings.avatarId
  if (settings.avatarSrc) out.avatarSrc = settings.avatarSrc
  if (typeof settings.expressionPresetId !== 'undefined') {
    out.expressionPresetId = String(settings.expressionPresetId || 'expressive')
  }
  if (typeof settings.expressionStrength !== 'undefined') {
    out.expressionStrength = toFinite(settings.expressionStrength, 2.46)
  }
  if (typeof settings.faceFitStrength !== 'undefined') {
    out.faceFitStrength = toFinite(settings.faceFitStrength, 0.72)
  }
  if (typeof settings.mouthBoost !== 'undefined') {
    out.mouthBoost = toFinite(settings.mouthBoost, 3.15)
  }
  if (typeof settings.mouthSmoothing !== 'undefined') {
    out.mouthSmoothing = toFinite(settings.mouthSmoothing, 0.42)
  }
  if (typeof settings.browStrength !== 'undefined') {
    out.browStrength = toFinite(settings.browStrength, 1.56)
  }
  if (typeof settings.browSmoothing !== 'undefined') {
    out.browSmoothing = toFinite(settings.browSmoothing, 0.34)
  }
  if (typeof settings.cheekStrength !== 'undefined') {
    out.cheekStrength = toFinite(settings.cheekStrength, 3.58)
  }
  if (typeof settings.cheekSmoothing !== 'undefined') {
    out.cheekSmoothing = toFinite(settings.cheekSmoothing, 0.32)
  }
  if (typeof settings.precomputedAvatarCutoutSrc !== 'undefined') {
    out.precomputedAvatarCutoutSrc = settings.precomputedAvatarCutoutSrc || null
  }
  if (typeof settings.precomputedAvatarHairMaskSrc !== 'undefined') {
    out.precomputedAvatarHairMaskSrc = settings.precomputedAvatarHairMaskSrc || null
  }
  if (typeof settings.debugLogs !== 'undefined') {
    out.debugLogs = settings.debugLogs === true
  }
  return out
}

function toSafeTransformOptions(settings = {}) {
  const out = toTransformOptions(settings)
  // Ensure we don't accidentally hide the camera layer if settings are incomplete
  const lv = out.layerVisibility || {}

  // Defaults matching legacy VIDEO_CHAT_DEFAULTS
  if (typeof lv.background === 'undefined') lv.background = true
  if (typeof lv.camera === 'undefined') lv.camera = true
  if (typeof lv.avatarFace === 'undefined') lv.avatarFace = true
  if (typeof lv.avatarEyes === 'undefined') lv.avatarEyes = true
  if (typeof lv.avatarHair === 'undefined') lv.avatarHair = true // Legacy: true

  out.layerVisibility = lv

  // Legacy defaults
  if (typeof out.useSegmentation === 'undefined') out.useSegmentation = true
  if (typeof out.expressionPresetId === 'undefined') out.expressionPresetId = 'expressive'
  if (typeof out.expressionStrength === 'undefined') out.expressionStrength = 2.46
  if (typeof out.faceFitStrength === 'undefined') out.faceFitStrength = 0.72
  if (typeof out.mouthBoost === 'undefined') out.mouthBoost = 3.15
  if (typeof out.mouthSmoothing === 'undefined') out.mouthSmoothing = 0.42
  if (typeof out.browStrength === 'undefined') out.browStrength = 1.56
  if (typeof out.browSmoothing === 'undefined') out.browSmoothing = 0.34
  if (typeof out.cheekStrength === 'undefined') out.cheekStrength = 3.58
  if (typeof out.cheekSmoothing === 'undefined') out.cheekSmoothing = 0.32

  // Ensure output format matches legacy defaults
  if (!out.output) {
    out.output = {
      width: 640,
      height: 360,
      fps: 24
    }
  }

  // Only allow engine debug logs in dev (or explicit runtime debug flag).
  if (typeof out.debugLogs === 'boolean') {
    out.debugLogs = out.debugLogs === true && isDebugLogEnabled('avatar')
  } else {
    out.debugLogs = isDebugLogEnabled('avatar')
  }
  return out
}

function toFinite(value, fallback = 0) {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

async function waitForFirstFrame(stream, timeoutMs = 1500) {
  try {
    const v = document.createElement('video')
    v.muted = true
    v.playsInline = true
    v.srcObject = stream
    const done = new Promise((resolve) => {
      const onReady = () => resolve(true)
      v.onloadeddata = onReady
      v.onplaying = onReady
      // v.onloadedmetadata = onReady // Wait for actual data/playback, metadata is not enough for canvas streams
      setTimeout(() => resolve(false), timeoutMs)
    })
    try {
      await v.play().catch(() => {})
    } catch {}
    const ok = await done
    try {
      v.srcObject = null
    } catch {}
    return ok
  } catch {
    return false
  }
}
